import { useActor, useMachine } from '@xstate/react';
import { isString } from 'lodash';
import { useEffect, useContext, useMemo, ReactElement } from 'react';
import {
  AnyActorRef,
  AnyInterpreter,
  AnyState,
  AnyStateMachine,
  InvokeConfig,
  StateFrom,
} from 'xstate';
import { MachineScreenStates, ScreenMapCopy, ScreenMapMatchesFunnel } from '~/machines/utils/types';
import { ParsedQueryParams, QueryParamsContext } from '~/providers/query-params';
import { IS_PRODUCTION } from '~/utils/constants';
import { store } from '~/utils/store';
import { isStorybook } from '~/utils/storybook';
import { emptyActor, parseState } from '~/utils/xstate';
import { useScreen } from './use-screen';

/*
  This hook accepts a "funnel machine".
  
  It starts the machine w/ any persisted context found in the store (specific to that funnel)
  plus the query params from the QueryParamsProvider.

  It persists the machine state (StateFrom<TMachine>) to the store every time the machine transitions states,
  and it persists the active funnel id and active route.
  
  If the funnel contains micro machines, it sets up a listener for the active micro machine
  and returns the internal state of that micro machine (in addition to the internal state of the funnel machine).

  If the hook is passed a screen map and copy, it will return the react Screen component
  and copy that pairs with the current state.
*/

export const useFunnel = <
  TMachine extends AnyStateMachine,
  TScreenMap extends ScreenMapMatchesFunnel<TMachine>,
  TCopy extends ScreenMapCopy<TScreenMap>,
>({
  funnelMachine,
  screenMap,
  copy,
}: {
  funnelMachine: () => TMachine;
  screenMap?: TScreenMap;
  copy?: TCopy;
}) => {
  const machine = useMemo(() => funnelMachine(), [funnelMachine]);
  const queryParams = useContext(QueryParamsContext);

  useEffect(() => {
    persistActiveFunnel(machine);
  }, [machine]);

  const [funnelState, funnelSend, service] = useMachine(machine, {
    devTools: !IS_PRODUCTION,
    context: getStarterContext({ queryParams, machine }),
  });

  const serviceMap = useMemo(() => getStateMap(machine), [machine]);
  const activeMicro = getActiveMicro(funnelState, serviceMap);
  const [microState, microSend] = useActor((activeMicro || emptyActor) as AnyActorRef);

  const step = getStepObject({ funnelState, microState, machine });
  const [Screen, screenCopy] = useScreen({ screenMap: screenMap || {}, overrideCopy: copy, step });

  useEffect(() => {
    persistStateOnTransition(service);
  }, [service]);

  return {
    funnel: {
      state: funnelState,
      send: funnelSend,
      context: funnelState.context as StateFrom<TMachine>['context'],
      matches: funnelState.matches as StateFrom<TMachine>['matches'],
    },
    micro: microState
      ? {
          state: microState as AnyState,
          send: microSend,
          context: microState.context as AnyState['context'],
          matches: microState.matches as AnyState['matches'],
        }
      : undefined,
    step,
    overrideCopy: screenCopy,
    Screen: Screen as (props: {
      funnel: object;
      micro?: object;
      overrideCopy?: any;
    }) => ReactElement,
  };
};

const getStarterContext = ({
  queryParams,
  machine,
}: {
  queryParams: ParsedQueryParams;
  machine: AnyStateMachine;
}) => {
  const persistedContext = store.getFunnel(machine.id)?.context || {};
  return { ...persistedContext, queryParams: { ...persistedContext.queryParams, ...queryParams } };
};

const getStateMap = <TMachine extends AnyStateMachine>(machine: TMachine) => {
  const map = Object.keys(machine.states).reduce((hash, stateName) => {
    const invokedService = machine.states[stateName].config.invoke as InvokeConfig<any, any>;
    const serviceId = invokedService?.id || invokedService?.src;
    const isMicro =
      isString(serviceId) && !!(machine.options.services?.[serviceId] as any)?.machine;
    return { ...hash, [stateName]: { serviceId, isMicro } };
  }, {});
  return map as Record<MachineScreenStates<TMachine>, { serviceId?: string; isMicro: boolean }>;
};

const getActiveMicro = (
  currentFunnelState: AnyState,
  serviceMap: ReturnType<typeof getStateMap>,
) => {
  const activeState = serviceMap[parseState(currentFunnelState).parentState];
  const activeMicro =
    activeState?.isMicro && currentFunnelState.children[activeState.serviceId || ''];
  return activeMicro as AnyActorRef;
};

const getStepObject = ({
  funnelState,
  microState,
  machine,
}: {
  funnelState: AnyState;
  microState?: AnyState;
  machine: AnyStateMachine;
}) => {
  const parentStep = parseState(funnelState).parentState;
  const childStep = microState ? parseState(microState).parentState : undefined;
  const grandChildStep = microState ? parseState(microState).childState : undefined;

  const activeStep = childStep || parentStep;
  const step = {
    active: activeStep,
    child: childStep,
    parent: parentStep,
    grandChild: grandChildStep,
    testId: `${machine.id}-${activeStep}`,
    funnelId: machine.id,
  };
  return step;
};

const persistActiveFunnel = (machine: { id: string }) => {
  if (isStorybook()) return;
  store.set('ACTIVE_FUNNEL_ROUTE', window.location.pathname);
  store.set('ACTIVE_FUNNEL_ID', machine.id);
};

const persistStateOnTransition = (service: AnyInterpreter): void => {
  service.onTransition(state => {
    store.setFunnel(service.machine.id, state);
  });
};
